import React from 'react';

import cs from 'classnames';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {TooltipList} from 'app/components/general/TooltipList';
import {useCurrentUserState} from 'app/current-user/api';
import StateButton from 'app/general/widgets/StateButton';
import {useAddReaction, useRemoveReaction} from 'app/reactions/api';
import {useUserReactedMap} from 'app/reactions/hooks';
import {
  filterSkinToneReactions,
  modifyReaction,
  skinToneableReactions,
} from 'app/reactions/utils';
import {gt} from 'app/utils/i18n';

import type {
  CommentReactionsType,
  CommentReactionTypeWithTone,
  CommentSerializedType,
} from 'sweet-types';

interface Props {
  reactions: CommentReactionsType;
  comment: CommentSerializedType;
}

export const ReactionsCounterButtons = ({
  reactions,
  comment,
}: Props): JSX.Element => {
  const availableSkinTonedReaction = filterSkinToneReactions(reactions);
  const userReactionsMap = useUserReactedMap(reactions);
  const userSkinName = useCurrentUserState().data?.emoji_skin_tone || undefined;
  const [addReaction] = useAddReaction();
  const [removeReaction] = useRemoveReaction();

  return (
    <div className="btn-group flex-wrap">
      {availableSkinTonedReaction.map(
        ({icon, title, slug, type, modifier, users}) =>
          reactions[slug] != null ? (
            <LazyTooltip
              key={`${slug}`}
              tooltip={
                <TooltipList
                  title={<span>{gt('Reacted with %s:', title)}</span>}
                  items={users}
                />
              }
            >
              <StateButton
                onClick={() => {
                  const iconToneData = slug.split('::');
                  const name =
                    userSkinName &&
                    (skinToneableReactions.includes(icon) ||
                      iconToneData.length > 1)
                      ? (`${type}::${userSkinName}` as CommentReactionTypeWithTone)
                      : type;

                  const toggleReaction =
                    !userReactionsMap[name] === true
                      ? addReaction
                      : removeReaction;
                  return toggleReaction({
                    content_type: 'commentwithmeta',
                    object_id: comment.id,
                    name,
                  });
                }}
                busyText={
                  <small className="text-dark">{reactions[slug].length}</small>
                }
                className={cs(
                  'btn btn-sm btn-outline-light px-1 mb-1 py-0 d-flex flex-wrap flex-grow-0 align-items-center justify-content-center',
                  {
                    active: userReactionsMap[slug],
                  }
                )}
              >
                <span>{modifyReaction(icon, modifier.tone)}</span>{' '}
                <small className="mx-1 text-dark">{users.length}</small>
              </StateButton>
            </LazyTooltip>
          ) : null
      )}
    </div>
  );
};
